import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { adminPlatformAPI } from '../services/api';
import type { SiteHealthSummary } from '../services/types';
import { useAuth } from './AuthContext';

interface SiteContextType {
  sites: SiteHealthSummary[];
  activeSite: SiteHealthSummary | null;
  activeSiteId: number | null;
  setActiveSiteId: (id: number | null) => void;
  loading: boolean;
  refreshSites: () => Promise<void>;
}

const SiteContext = createContext<SiteContextType>({
  sites: [],
  activeSite: null,
  activeSiteId: null,
  setActiveSiteId: () => {},
  loading: true,
  refreshSites: async () => {},
});

export const useSite = () => useContext(SiteContext);

const STORAGE_KEY = 'uitl_active_site';

export const SiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { activeOrg, isAuthenticated } = useAuth();
  const [sites, setSites] = useState<SiteHealthSummary[]>([]);
  const [activeSiteId, setActiveSiteIdRaw] = useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number(stored) : null;
  });
  const [loading, setLoading] = useState(true);

  const refreshSites = useCallback(async () => {
    if (!isAuthenticated) {
      setSites([]);
      setLoading(false);
      return;
    }
    try {
      const res = await adminPlatformAPI.getSites();
      setSites(res.data);
    } catch {
      setSites([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Load sites on mount and when org changes
  useEffect(() => {
    setLoading(true);
    refreshSites();
  }, [refreshSites, activeOrg?.id]);

  // Reset selection when org changes
  useEffect(() => {
    setActiveSiteIdRaw(null);
    localStorage.removeItem(STORAGE_KEY);
  }, [activeOrg?.id]);

  const setActiveSiteId = useCallback((id: number | null) => {
    setActiveSiteIdRaw(id);
    if (id !== null) {
      localStorage.setItem(STORAGE_KEY, String(id));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Validate stored ID against loaded sites
  const activeSite = useMemo(() => {
    if (activeSiteId === null) return null;
    return sites.find(s => s.id === activeSiteId) || null;
  }, [activeSiteId, sites]);

  // If stored site ID doesn't match any loaded site, clear it
  useEffect(() => {
    if (!loading && activeSiteId !== null && sites.length > 0 && !activeSite) {
      setActiveSiteId(null);
    }
  }, [loading, activeSiteId, sites, activeSite, setActiveSiteId]);

  return (
    <SiteContext.Provider value={{
      sites,
      activeSite,
      activeSiteId,
      setActiveSiteId,
      loading,
      refreshSites,
    }}>
      {children}
    </SiteContext.Provider>
  );
};
